import { useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import type {
  DiscountDisplayType,
} from './PriceDetails'
import PriceDetails from './PriceDetails'

import BodyText from 'LuxKit/Typography/BodyText'
import { MALDIVES_CHARTER_END_TARGET_DATE, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'
import { FlightsClassTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import {
  extractPriceAndValue,
  getDurationLabel,
} from 'tripPlanner/utils/bookmarkCard'

const discountDisplayTypeMapping: Utils.PartialRecord<
  App.OfferType,
  DiscountDisplayType
> = {
  hotel: 'badge',
  tactical_ao_hotel: 'badge',
  bundle_and_save: 'none',
  rental: 'none',
}

interface Props {
  offer: App.Offer | App.OfferSummary
  package?: App.Package
  depositComponent?: React.ReactElement
  className?: string
  isCharterBundleOffer?: boolean
  recommendation?: App.Recommendation
  duration?: number
}

const defaultOccupancies: Array<App.Occupants> = [{ adults: 2 }]

function LEOfferPriceDetails({
  depositComponent,
  offer,
  package: pkg = offer.lowestPricePackage,
  className,
  isCharterBundleOffer = false,
  recommendation,
  duration = -1,
}: Props) {
  const intl = useIntl()
  const defaultAirport = useDefaultAirport()
  const flightPrice = offer.flightPrices[defaultAirport?.code ?? '']
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const effectiveFareClass = isCharterBundleOffer
    ? (resolveAllowedFareClass({
        selectedFareClass: FlightsClassTypes.ECONOMY,
        campaignTags: offer.campaignTags,
        occupancies: defaultOccupancies,
      }) ?? FlightsClassTypes.ECONOMY)
    : FlightsClassTypes.ECONOMY
  const { totalPrice: charterFlightPrice } = useCharterFlightPrice({
    charterBundleRoute,
    checkInDate: MALDIVES_CHARTER_START_TARGET_DATE,
    checkOutDate: MALDIVES_CHARTER_END_TARGET_DATE,
    occupancies: isCharterBundleOffer ? defaultOccupancies : EmptyArray,
    fareClass: effectiveFareClass,
    disabled: !isCharterBundleOffer || !charterBundleRoute,
  })

  const { currentCurrency } = useContext(GeoContext)

  if (!pkg) {
    return null // TODO handle this
  }

  const { durationLabel } = pkg

  let { price, luxPlusPrice, value } = extractPriceAndValue({
    recommendation,
    pkg,
  })

  let priceLabel = getDurationLabel({
    durationLabel,
    duration,
    recommendation,
  })

  if (isCharterBundleOffer && charterBundleRoute) {
    priceLabel = intl.formatMessage(
      {
        id: 'gEFgViLaxzHaUoh9q6IV',
        defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
      },
      { count: pkg.duration },
    )

    price += charterFlightPrice
    if (luxPlusPrice) {
      luxPlusPrice += charterFlightPrice
    }
    value += charterFlightPrice
  }

  if (!isCharterBundleOffer && offer.bundledWithFlightsOnly) {
    if (typeof flightPrice !== 'number') {
      // If `bundledWithFlightsOnly` is true, we *have* to show the price with flights for contractual reasons.
      // So if we don't have the flight price for whatever reason, then we just can't show the price here.
      return (
        <BodyText variant="small" weight="normal">
          {intl.formatMessage(
            {
              id: 'VjbJ0mClKLVcJbIRcZ0Y',
              defaultMessage: '{duration} + flights',
            },
            { duration: offer.durationLabel.toLocaleLowerCase() },
          )}
        </BodyText>
      )
    }

    priceLabel = intl.formatMessage(
      {
        id: 'LqWfLDafPZIWl7G7C9Ug',
        defaultMessage: '{duration} + flights from',
      },
      { duration: durationLabel.toLocaleLowerCase() },
    )
    price += flightPrice
    if (luxPlusPrice) {
      luxPlusPrice += flightPrice
    }
    value += flightPrice
  }

  const discountPercent = getValueOffPercent(value, price)
  const { shouldShowDiscount } = getOfferValueDisplayVisibility({ discountPercent })

  return (
    <PriceDetails
      currencyCode={currentCurrency}
      price={price}
      luxPlusPrice={luxPlusPrice}
      value={value}
      discount={shouldShowDiscount ? discountPercent : undefined}
      priceLabel={priceLabel}
      depositComponent={depositComponent}
      discountDisplayType={discountDisplayTypeMapping[offer.type] ?? 'none'}
      className={className}
    />
  )
}

export default LEOfferPriceDetails
